<template>
	<div class="user-detail-page" :class="{ 'user-detail-page--embedded': embedded }">
		<a-page-header
			v-if="!embedded"
			@back="goBack"
			style="background-color: #fff; padding:24px 24px 0 24px;"
			:sub-title="userInfo.id ? `ID: ${userInfo.id}` : ''"
		>
			<template slot="extra">
				<a-button size="small">修改</a-button>
				<a-button size="small">更多</a-button>
			</template>
		</a-page-header>

		<a-spin :spinning="loading">
			<div class="detail-content">
				<template v-if="!loading && userInfo.id">
					<!-- 1. 顶部：头像 | 昵称/徽章/基本信息/个性签名 | 相册（同一行） -->
					<div class="detail-section profile-header">
						<div class="profile-header-row">
							<div class="profile-header-col profile-header-col-avatar">
								<div class="avatar-wrap">
									<img v-if="userInfo.avatarUrl" :src="userInfo.avatarUrl" class="avatar" alt="头像" />
									<div v-else class="avatar avatar-placeholder">暂无头像</div>
								</div>
							</div>
							<div class="profile-header-colprofile-header-col-middle">
								<div class="profile-name-line">
									<span v-if="userInfo.isVest === 'Y'" class="tag-vest">【马甲】</span>
									<span class="nickname">{{ userInfo.nickname || '-' }}</span>
									<span :class="['badge', userInfo.isCertified === 'Y' ? 'badge-certified' : 'badge-uncertified']">
										{{ userInfo.isCertified === 'Y' ? '已认证' : '未认证' }}
									</span>
									<span class="badge badge-vip" v-if="userInfo.isVip === 'Y'">VIP</span>
									<span
										:class="['voice-bar', { 'voice-bar--disabled': !voiceUrl, 'voice-bar--playing': voicePlaying }]"
										@click="toggleVoice"
									>
										<span class="voice-bar-btn">
											<span v-if="!voicePlaying" class="voice-bar-play-icon" />
											<span v-else class="voice-bar-pause-icon" />
										</span>
										<span class="voice-bar-strip">
											<span v-for="i in 5" :key="i" class="voice-bar-dot" :style="{ height: waveHeights[i - 1] + 'px' }" />
										</span>
									</span>
									<audio
										ref="voiceAudio"
										:src="voiceUrl"
										@play="voicePlaying = true"
										@pause="voicePlaying = false"
										@ended="voicePlaying = false"
									/>
								</div>
								<div class="profile-basic">
									<span>{{ userInfo.sex === 'MALE' ? '男' : userInfo.sex === 'FEMALE' ? '女' : '-' }}</span>
									<span>{{ userInfo.age != null ? userInfo.age + '岁' : '-' }}</span>
									<span>单身</span>
									{{ [userInfo.province, userInfo.city, userInfo.area].filter(Boolean).join('-') || '-' }}
								</div>
								<div class="profile-signature" v-if="userInfo.signature">{{ userInfo.signature }}</div>
								<div class="profile-signature placeholder" v-else>个性签名</div>
							</div>
							<div class="profile-header-col profile-header-col-photo">
								<div class="photo-gallery photo-gallery-in-header">
									<div class="photo-item" v-for="item in albumList" :key="item.id">
										<x-image :src="item.photoUrl" :width="80" :height="100" />
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- 支出/收入图：与 profile-header 同级，便于预览布局效果 -->
					<div class="detail-section chart-section">
						<a-card class="detail-card chart-card" :bordered="false">
							<a-tabs :activeKey="chartTabKey" @change="onChartTabChange" size="small">
								<a-tab-pane key="expense" tab="支出">
									<div ref="chartExpenseRef" class="chart-container" />
								</a-tab-pane>
								<a-tab-pane key="income" tab="收入">
									<div ref="chartIncomeRef" class="chart-container" />
								</a-tab-pane>
							</a-tabs>
						</a-card>
					</div>

					<div class="detail-section-main">
						<a-card class="detail-card">
							<div class="detail-grid">
								<div class="detail-item"><span class="label">账号:</span><span class="val">{{ userInfo.account || '-' }}</span></div>
								<div class="detail-item"><span class="label">金币:</span><span class="val">{{ userInfo.gold != null ? userInfo.gold : '-' }}</span></div>
								<div class="detail-item"><span class="label">积分:</span><span class="val">{{ userInfo.score != null ? userInfo.score : '-' }}</span></div>
								<div class="detail-item"><span class="label">充值:</span><span class="val">{{ userInfo.rechargeAmount }}元 / {{ userInfo.rechargeNum != null ? userInfo.rechargeNum : 0 }}次</span></div>

								<div class="detail-item"><span class="label">魅力:</span><span class="val">{{ userInfo.charmLevel || '-' }}级 / {{ userInfo.charm != null ? userInfo.charm : '-' }}</span></div>
								<div class="detail-item"><span class="label">加权:</span><span class="val">{{ userInfo.weighting != null ? userInfo.weighting : '-' }}</span></div>
								<div class="detail-item"><span class="label">会员:</span><span class="val">{{ userInfo.isVip === 'Y' ? '是' : '否' }} / {{ userInfo.vipExpireTime || '-' }}</span></div>
								<div class="detail-item"><span class="label">注册时间:</span><span class="val">{{ userInfo.created || '-' }}</span></div>

								<div class="detail-item"><span class="label">身高:</span><span class="val">{{ userInfo.height || '-' }}</span></div>
								<div class="detail-item"><span class="label">学历:</span><span class="val">{{ dictLabel(dict.qualification, userInfo.qualification) }}</span></div>
								<div class="detail-item"><span class="label">工作:</span><span class="val">{{ userInfo.workName || '-' }}</span></div>
								<div class="detail-item"><span class="label">年收入:</span><span class="val">{{ dictLabel(dict.income, userInfo.income) }}</span></div>

								<div class="detail-item"><span class="label">渠道:</span><span class="val">{{ userInfo.spreadChannelName || '-' }}</span></div>
								<div class="detail-item"><span class="label">邀请人:</span><span class="val">{{ userInfo.parentUserNickname || '-' }}{{ userInfo.inviteCount != null ? ` (${userInfo.inviteCount})` : '' }}</span></div>
								<div class="detail-item"><span class="label">手机:</span><span class="val">{{ formatPhone(userInfo) }}</span></div>
								<div class="detail-item"><span class="label">手机号:</span><span class="val">{{ userInfo.phone || userInfo.mobile || '-' }}</span></div>

								<div class="detail-item"><span class="label">提现:</span><span class="val">{{ userInfo.cashoutAmount }}元/{{ userInfo.cashoutNum != null ? userInfo.cashoutNum : 0 }}次</span></div>
								<div class="detail-item"><span class="label">活跃时间:</span><span class="val">{{ userInfo.activeTime || '-' }}</span></div>
								<div class="detail-item"><span class="label">生日:</span><span class="val">{{ userInfo.birthday || '-' }}</span></div>
								<div class="detail-item"><span class="label">推送:</span><span class="val">{{ userInfo.isOpenPush === 'Y' ? '开启' : '关闭' }} / {{ userInfo.uniPushId || '-' }}</span></div>
							</div>
						</a-card>

						<!-- 聊天统计 -->
						<a-card class="detail-card" :title="`聊天 (${userConversationInfo.conversationNum != null ? userConversationInfo.conversationNum : 0})`">
							<div class="chat-grid">
								<div class="detail-item"><span class="label">消息:</span><span class="val">{{ userConversationInfo.sendMessageNum || '-' }}/{{ userConversationInfo.messageNum || '-' }}</span></div>
								<div class="detail-item"><span class="label">语音通话:</span><span class="val">{{ userConversationInfo.voletDuration || '-' }}/{{ userConversationInfo.voletNum }}次</span></div>
								<div class="detail-item"><span class="label">视频通话:</span><span class="val">{{ userConversationInfo.viletDuration || '-' }}/{{ userConversationInfo.viletNum }}次</span></div>
								<div class="detail-item"><span class="label">赠送礼物:</span><span class="val">{{ userConversationInfo.donateGiftNum }}次/{{ userConversationInfo.donateGiftAmount }}元</span></div>
								<div class="detail-item"><span class="label">主叫语音:</span><span class="val">{{ userConversationInfo.callVoletDuration }}/{{ userConversationInfo.callVoletNum }}次</span></div>
								<div class="detail-item"><span class="label">主叫视频:</span><span class="val">{{ userConversationInfo.callViletDuration || '-' }}/{{ userConversationInfo.callViletNum }}次</span></div>
								<div class="detail-item"><span class="label">接收礼物:</span><span class="val">{{ userConversationInfo.receiveGiftNum }}次/{{ userConversationInfo.receiveGiftAmount }}元</span></div>
								<div class="detail-item"><span class="label">被叫语音:</span><span class="val">{{ userConversationInfo.calledVoletDuration }}/{{ userConversationInfo.calledVoletNum }}次</span></div>
								<div class="detail-item"><span class="label">被叫视频:</span><span class="val">{{ userConversationInfo.calledViletDuration || '-' }}/{{ userConversationInfo.calledViletNum }}次</span></div>
							</div>
						</a-card>

						<!-- Tab 切换 + 列表（占满整行） -->
						<a-card class="detail-card record-tabs-card record-tabs-card-full">
                            <a-tabs :activeKey="recordTab" @change="onRecordTabChange">
                                <a-tab-pane key="conversation" tab="会话记录" />
                                <a-tab-pane key="recharge" tab="充值记录" />
                                <a-tab-pane key="withdraw" tab="提现记录" />
                                <a-tab-pane key="gold" tab="金币流水" />
                                <a-tab-pane key="score" tab="积分流水" />
                                <a-tab-pane key="invite" tab="邀请记录" />
                            </a-tabs>
                            <a-table
                                :columns="recordTableColumns"
                                :dataSource="recordList"
                                :rowKey="(r, i) => r.id || i"
                                :loading="recordLoading"
                                :pagination="false"
                                size="small"
                                bordered
                            >
                                <template slot="time" slot-scope="text, record">
                                    <div style="display: flex; flex-direction: column;" v-if="record.updateTime || record.created">
                                        <span v-if="record.updateTime">上次: {{ record.updateTime }}</span>
                                        <span v-if="record.created">创建: {{ record.created }}</span>
                                    </div>
                                    <span v-else>-</span>
                                </template>
                                <template slot="action" slot-scope="text, record">
                                    <a @click="onViewRecord(record)">查看</a>
                                </template>
                                <template slot="user" slot-scope="text, record">
                                    <div class="record-user-cell">
                                        <img v-if="record.toUserAvatarUrl" :src="record.toUserAvatarUrl" class="record-user-avatar" alt="" />
                                        <span v-else class="record-user-avatar-placeholder">头像</span>
                                        <span class="record-user-info">
                                            <span v-if="record.isVest === 'Y'">【马甲】</span>{{ record.toUserNickname }}
                                            <span class="record-user-meta">{{ record.toUserAge }}岁 {{ record.toUserProvince }}-{{ record.toUserCity }}</span>
                                        </span>
                                    </div>
                                </template>
								<template slot="message" slot-scope="text, record">
                                    <span>{{ record.unreadCount }}</span>
									<span> / </span>
									<span>{{ record.messageNum }}条</span>
                                </template>
								<template slot="volet" slot-scope="text, record">
									<span>{{ record.voletDuration }}</span>
									<span> / </span>
									<span style="color: #999;">{{ record.voletNum }}次</span>
								</template>
								<template slot="vilet" slot-scope="text, record">
									<span>{{ record.viletDuration }}</span>
									<span> / </span>
									<span>{{ record.viletNum }}次</span>
								</template>
								<template slot="gift" slot-scope="text, record">
									<span>{{ record.giftNum }}</span>
									<span> / </span>
									<span>{{ record.giftAmount }}元</span>
								</template>

                                <!-- 充值记录 -->
                                <template slot="rechargeTyp" slot-scope="text, record">
                                    <span v-if="(record.channel || record.payType) === 'ALIPAY'">支付宝</span>
                                    <span v-else>微信</span>
                                </template>
                                <template slot="rechargeOrder" slot-scope="text, record">
                                    <div class="record-complex">
                                        <p>订单号：{{ record.orderId }}</p>
                                        <p class="assist">流水号：{{ record.tradeId }}</p>
                                    </div>
                                </template>
                                <template slot="rechargePay" slot-scope="text, record">
                                    <div class="record-complex">
                                        <p>
                                            <span style="margin-right: 10px;">{{ record.payAmount }}元</span>
                                            <span class="success" v-if="record.payStatus == 'PAID'">已支付</span>
                                            <span class="error" v-else-if="record.payStatus == 'FAILD'">支付失败</span>
                                            <span class="wait" v-else>未支付</span>
                                        </p>
                                        <p class="assist" v-if="record.payTime">{{ record.payTime }}</p>
                                    </div>
                                </template>
                                <template slot="rechargeCallback" slot-scope="text, record">
                                    <div class="record-complex">
                                        <p>
                                            <span class="success" v-if="record.isCallback === 'Y'">已回调</span>
                                            <span class="wait" v-else>未回调</span>
                                        </p>
                                        <p class="assist" v-if="record.callbackTime">{{ record.callbackTime }}</p>
                                    </div>
                                </template>
                                <template slot="rechargeRecharge" slot-scope="text, record">
                                    <div class="record-complex">
                                        <p>
                                            <span style="margin-right: 10px;">{{ record.payAmount }}元</span>
                                            <span class="success" v-if="record.isRecharge === 'Y'">已充值</span>
                                            <span class="wait" v-else>未支付</span>
                                        </p>
                                        <p class="assist" v-if="record.rechargeTime">{{ record.rechargeTime }}</p>
                                    </div>
                                </template>
                                <!-- 提现记录 -->
                                <template slot="withdrawApply" slot-scope="text, record">
                                    <div class="record-complex">
                                        <p>{{ record.payAmount }}元</p>
                                        <p class="assist">{{ record.created }}</p>
                                    </div>
                                </template>
                                <template slot="withdrawPay" slot-scope="text, record">
                                    <div class="record-complex">
                                        <p>
                                            <span style="margin-right: 10px;">{{ record.payAmount }}元</span>
                                            <span class="success" v-if="record.payStatus == 'PAID'">已支付</span>
                                            <span class="error" v-else-if="record.payStatus == 'FAILD'">支付失败</span>
                                            <span class="wait" v-else>未支付</span>
                                        </p>
                                    </div>
                                </template>
                                <template slot="withdrawFee" slot-scope="text, record">
                                    <div class="record-complex" v-if="record.fee">{{ record.fee }}元</div>
                                    <span v-else>-</span>
                                </template>
                                <template slot="withdrawFeeRate" slot-scope="text, record">
                                    <div class="record-complex" v-if="record.feeRate">{{ record.feeRate }}%</div>
                                    <span v-else>-</span>
                                </template>
                                <template slot="withdrawStatus" slot-scope="text, record">
                                    <span class="error" v-if="record.payStatus === 'FAILD'">支付失败</span>
                                    <span class="success" v-else-if="record.status === 'PAID'">已支付</span>
                                    <span class="wait" v-else>待处理</span>
                                </template>
                                <!-- 金币/积分流水（与菜单页字段一致） -->
                                <template slot="goldSubject" slot-scope="text, record">
                                    <span>{{ dictLabel(dict.gold, record.subject) || record.subjectText || '-' }}</span>
                                </template>
                                <template slot="scoreSubject" slot-scope="text, record">
                                    <span>{{ dictLabel(dict.score, record.subject) || record.subjectText || '-' }}</span>
                                </template>
                                <template slot="goldChangeAmount" slot-scope="text, record">
                                    <span v-if="record.amount >= 0" class="income-span">{{ record.amount }}</span>
                                    <span v-else class="expend-span">{{ record.amount }}</span>
                                </template>
                                <template slot="scoreChangeAmount" slot-scope="text, record">
                                    <span v-if="record.amount > 0" class="income-span">{{ record.amount }}</span>
                                    <span v-else class="expend-span">{{ record.amount }}</span>
                                </template>
                            </a-table>
                            <div class="x-pagination">
                                <a-pagination
                                    :pageSize="recordPageSize"
                                    v-model="recordPageIndex"
                                    :total="recordTotal"
                                    @change="recordPageChange"
                                    :hideOnSinglePage="true"
                                    show-less-items
                                />
                            </div>
                        </a-card>
					</div>
				</template>
				<template v-else-if="!loading && !userInfo.id">
					<a-empty description="未找到该用户或暂无详情" />
				</template>
			</div>
		</a-spin>

		<conversation-message-drawer
			:visible.sync="conversationMessageDrawerVisible"
			:conversation-id="conversationMessageDrawerConversationId"
			:from-user-info="conversationMessageDrawerFromUser"
			:to-user-info="conversationMessageDrawerToUser"
			:title="conversationMessageDrawerTitle"
		/>
	</div>
</template>

<script>
	import * as echarts from 'echarts';
	import { userDetail, userRechargeList, userCashoutList } from '@/api/member/user';
	import { userGoldList, userScoreList } from '@/api/op/user';
	import { conversationList } from '@/api/op/conversation';
	import XImage from "@/components/XImage.vue";
	import ConversationMessageDrawer from '@/components/ConversationMessageDrawer.vue';
	import dict from '@/config/dict';

	export default {
		name: 'UserDetail',
		components: {
			XImage,
			ConversationMessageDrawer,
		},
		props: {
			/** 嵌入抽屉时由父组件传入用户 ID；独立路由页则读 $route.query.id */
			id: {
				type: [Number, String],
				default: null
			},
			/** 嵌入模式：隐藏 page-header，适配抽屉内展示 */
			embedded: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				dict,
				loading: false,
				userInfo: {},
				albumList: [],
				userExpenseInfo: {},
				userIncomeInfo: {},
				userConversationInfo: {},
				recordTab: 'conversation',
				recordList: [],
				recordLoading: false,
				recordPageSize: 20,
				recordPageIndex: 1,
				recordTotal: 0,
				chartTabKey: 'expense',
				chartExpenseInstance: null,
				chartIncomeInstance: null,
				voicePlaying: false,
				waveHeights: [4, 8, 12, 8, 4],
				conversationMessageDrawerVisible: false,
				conversationMessageDrawerConversationId: 0,
				conversationMessageDrawerFromUser: {},
				conversationMessageDrawerToUser: {},
				conversationMessageDrawerTitle: '',
			};
		},
		computed: {
			userId() {
				if (this.id != null && this.id !== '') {
					return this.id;
				}
				return this.$route && this.$route.query ? this.$route.query.id : null;
			},
			voiceUrl() {
				return this.userInfo.voiceMotto || this.userInfo.voice_motto || '';
			},
			recordTableColumns() {
				const base = [
					{ title: '#', dataIndex: 'id', width: 60 },
					{ title: '用户', key: 'user', width: 180, scopedSlots: { customRender: 'user' } },
					{ title: '消息', key: 'message', width: 100, scopedSlots: { customRender: 'message' } },
					{ title: '语音', key: 'voice', width: 120, scopedSlots: { customRender: 'volet' } },
					{ title: '视频', key: 'video', width: 120, scopedSlots: { customRender: 'vilet' } },
					{ title: '礼物', key: 'gift', width: 100, scopedSlots: { customRender: 'gift' } },
					{ title: '时间', key: 'time', width: 180, scopedSlots: { customRender: 'time' } },
					{ title: '操作', key: 'action', width: 80, scopedSlots: { customRender: 'action' } }
				];
				if (this.recordTab === 'conversation') return base;
				if (this.recordTab === 'recharge') {
					return [
						{ title: '#', dataIndex: 'id', width: 60 },
						{ title: '充值渠道', key: 'typ', width: 90, scopedSlots: { customRender: 'rechargeTyp' } },
						{ title: '订单', key: 'order', width: 180, scopedSlots: { customRender: 'rechargeOrder' } },
						{ title: '支付', key: 'pay', width: 140, scopedSlots: { customRender: 'rechargePay' } },
						{ title: '回调', key: 'callback', width: 100, scopedSlots: { customRender: 'rechargeCallback' } },
						{ title: '充值', key: 'recharge', width: 140, scopedSlots: { customRender: 'rechargeRecharge' } },
						{ title: '备注', dataIndex: 'remark', width: 100 },
						{ title: '创建时间', dataIndex: 'created', width: 160 }
					];
				}
				if (this.recordTab === 'withdraw') {
					return [
						{ title: '#', dataIndex: 'id', width: 60 },
						{ title: '申请金额', key: 'apply', width: 120, scopedSlots: { customRender: 'withdrawApply' } },
						{ title: '支付', key: 'pay', width: 120, scopedSlots: { customRender: 'withdrawPay' } },
						{ title: '手续费', key: 'fee', width: 80, scopedSlots: { customRender: 'withdrawFee' } },
						{ title: '手续费率', key: 'feeRate', width: 80, scopedSlots: { customRender: 'withdrawFeeRate' } },
						{ title: '状态', key: 'status', width: 80, scopedSlots: { customRender: 'withdrawStatus' } },
						{ title: '备注', dataIndex: 'remark', width: 80 },
						{ title: '创建时间', dataIndex: 'created', width: 160 }
					];
				}
				if (this.recordTab === 'gold') {
					return [
						{ title: '#', dataIndex: 'id', width: 60 },
						{ title: '科目', key: 'goldSubject', width: 120, scopedSlots: { customRender: 'goldSubject' } },
						{ title: '变动金额', key: 'goldChangeAmount', width: 100, scopedSlots: { customRender: 'goldChangeAmount' } },
						{ title: '变动后余额', dataIndex: 'balanceAfter', width: 100 },
						{ title: '创建时间', dataIndex: 'created', width: 160 }
					];
				}
				if (this.recordTab === 'score') {
					return [
						{ title: '#', dataIndex: 'id', width: 60 },
						{ title: '科目', key: 'scoreSubject', width: 120, scopedSlots: { customRender: 'scoreSubject' } },
						{ title: '变动积分', key: 'scoreChangeAmount', width: 100, scopedSlots: { customRender: 'scoreChangeAmount' } },
						{ title: '变动后积分', dataIndex: 'balanceAfter', width: 100 },
						{ title: '创建时间', dataIndex: 'created', width: 160 }
					];
				}
				if (this.recordTab === 'invite') {
					return [
						{ title: '#', dataIndex: 'id', width: 60 },
						{ title: '用户', key: 'user', width: 180, scopedSlots: { customRender: 'user' } },
						{ title: '邀请时间', dataIndex: 'created', width: 160 },
						{ title: '操作', key: 'action', width: 80, scopedSlots: { customRender: 'action' } }
					];
				}
				return base;
			}
		},
		watch: {
			userId: {
				immediate: true,
				handler(id) {
					if (id) this.fetchDetail();
				}
			}
		},
		methods: {
			fetchDetail() {
				if (!this.userId) return;
				this.stopVoice();
				this.disposeCharts();
				this.loading = true;
				userDetail(this.userId)
					.then((res) => {
						this.userInfo = res.data.userInfo || {};
						this.albumList = res.data.albumList || [];
                        this.userExpenseInfo = res.data.userExpenseInfo || {};
                        this.userIncomeInfo = res.data.userIncomeInfo || {};
						this.userConversationInfo = res.data.userConversationInfo || {};
						this.fetchRecordList();
						// 等 v-if 渲染且布局完成后再初始化；抽屉动画后再 resize
						this.$nextTick(() => this.$nextTick(() => {
							this.initCharts();
							setTimeout(() => {
								this.initCharts();
								this.resizeCharts();
							}, this.embedded ? 320 : 120);
						}));
					})
					.catch(() => {
						this.userInfo = {};
						this.$message.error('获取用户详情失败');
					})
					.finally(() => {
						this.loading = false;
					});
			},
			disposeCharts() {
				if (this.chartExpenseInstance) {
					this.chartExpenseInstance.dispose();
					this.chartExpenseInstance = null;
				}
				if (this.chartIncomeInstance) {
					this.chartIncomeInstance.dispose();
					this.chartIncomeInstance = null;
				}
			},
			dictLabel(arr, value) {
				if (value == null || value === '') return '-';
				const item = (arr || []).find(d => d.value == value);
				return item ? item.label : value;
			},
			formatMoney(v) {
				return v != null && v !== '' ? v : '0';
			},
			formatPhone(u) {
				if (!u) return '-';
				const parts = [u.phoneOs || u.phone_os, u.phoneBrand || u.phone_brand, u.phoneModel || u.phone_model].filter(Boolean);
				return parts.length ? parts.join(' - ') : '-';
			},
			goBack() {
				this.stopVoice();
				this.$router.push('/member/userList');
			},
			toggleVoice() {
				if (!this.voiceUrl) {
					this.$message.info('暂无声音秀');
					return;
				}
				const el = this.$refs.voiceAudio;
				if (!el) return;
				try {
					if (this.voicePlaying) {
						el.pause();
					} else {
						el.play();
					}
				} catch (e) {
					this.$message.error('播放失败');
				}
			},
			stopVoice() {
				const el = this.$refs.voiceAudio;
				if (el) {
					el.pause();
					el.currentTime = 0;
				}
				this.voicePlaying = false;
			},
			onRecordTabChange(key) {
				this.recordTab = key;
				this.recordPageIndex = 1;
				this.fetchRecordList();
			},
			fetchRecordList() {
				if (!this.userId) return;
				const tab = this.recordTab;
				const page = this.recordPageIndex;
				const pageSize = this.recordPageSize;

				if (tab === 'invite') {
					const list = (this.userInfo.inviteList || []) || [];
					this.recordList = Array.isArray(list) ? list : [];
					this.recordTotal = this.recordList.length;
					this.recordLoading = false;
					return;
				}

				if (tab === 'conversation') {
					this.recordLoading = true;
					conversationList({ user: this.userId, page, pageSize }).then((res) => {
						this.recordList = (res.data && res.data.conversationList) || [];
						this.recordTotal = (res.data && res.data.totalRow) || 0;
						this.recordLoading = false;
					}).catch(() => {
						this.recordList = [];
						this.recordTotal = 0;
						this.recordLoading = false;
					});
					return;
				}

				this.recordLoading = true;
				const setList = (res) => {
					this.recordList = (res.data && res.data.recordList) || [];
					this.recordTotal = (res.data && res.data.totalRow) || 0;
					this.recordLoading = false;
				};
				const onErr = () => {
					this.recordList = [];
					this.recordTotal = 0;
					this.recordLoading = false;
				};

				if (tab === 'recharge') {
					userRechargeList({ id: this.userId, page, pageSize }).then(setList).catch(onErr);
					return;
				}
				if (tab === 'withdraw') {
					userCashoutList({ id: this.userId, page, pageSize }).then(setList).catch(onErr);
					return;
				}
				if (tab === 'gold') {
					userGoldList({ user: this.userId, page, pageSize }).then(setList).catch(onErr);
					return;
				}
				if (tab === 'score') {
					userScoreList({ user: this.userId, page, pageSize }).then(setList).catch(onErr);
					return;
				}
				this.recordLoading = false;
			},
			recordPageChange(current) {
				this.recordPageIndex = current;
				this.fetchRecordList();
			},
			onViewRecord(record) {
				if (this.recordTab === 'conversation' && record.conversationId != null) {
					// 打开聊天记录抽屉：当前详情用户为 from，会话对方为 to
					this.conversationMessageDrawerConversationId = record.conversationId;
					this.conversationMessageDrawerFromUser = {
						id: this.userInfo.id,
						avatar: this.userInfo.avatarUrl,
					};
					this.conversationMessageDrawerToUser = {
						id: record.toUserId,
						avatar: record.toUserAvatarUrl || '',
					};
					this.conversationMessageDrawerTitle = (this.userInfo.nickname || '') + '与' + (record.nickname || record.toUserNickname || '') + '对话';
					this.conversationMessageDrawerVisible = true;
					return;
				}
				this.$message.info('查看：' + (record.id || record.nickname || '记录'));
			},
			initCharts() {
				this.initExpenseChart();
				this.initIncomeChart();
			},
			initExpenseChart() {
				const el = this.$refs.chartExpenseRef;
				if (!el || this.chartExpenseInstance) {
                    return;
                }

				this.chartExpenseInstance = echarts.init(el);
				const option = {
					tooltip: { trigger: 'item' },
					legend: { bottom: 0, left: 'center' },
					series: [{
						type: 'pie',
						radius: ['35%', '60%'],
						center: ['50%', '45%'],
						avoidLabelOverlap: false,
						itemStyle: { borderColor: '#fff', borderWidth: 1 },
						label: { show: true, formatter: '{b}: {d}%' },
						data: [
								{ value: this.userExpenseInfo.messageCost, name: '消息' },
								{ value: this.userExpenseInfo.voletCost, name: '语音' },
								{ value: this.userExpenseInfo.viletCost, name: '视频' },
                                { value: this.userExpenseInfo.giftCost, name: '礼物' },
                                { value: this.userExpenseInfo.vipCost, name: '会员' },
							]
					}]
				};
				this.chartExpenseInstance.setOption(option);
				setTimeout(() => this.chartExpenseInstance && this.chartExpenseInstance.resize(), 0);
			},
			initIncomeChart() {
				const el = this.$refs.chartIncomeRef;
				if (!el || this.chartIncomeInstance) return;
				this.chartIncomeInstance = echarts.init(el);
				const option = {
					tooltip: { trigger: 'item' },
					legend: { bottom: 0, left: 'center' },
					series: [{
						type: 'pie',
						radius: ['35%', '60%'],
						center: ['50%', '45%'],
						avoidLabelOverlap: false,
						itemStyle: { borderColor: '#fff', borderWidth: 1 },
						label: { show: true, formatter: '{b}: {d}%' },
						data: [
                                { value: this.userIncomeInfo.messageCost, name: '消息' },
								{ value: this.userIncomeInfo.voletCost, name: '语音' },
								{ value: this.userIncomeInfo.viletCost, name: '视频' },
                                { value: this.userIncomeInfo.giftCost, name: '礼物' },
                                { value: this.userIncomeInfo.inviteCost, name: '邀请' },
							]
					}]
				};
				this.chartIncomeInstance.setOption(option);
				setTimeout(() => this.chartIncomeInstance && this.chartIncomeInstance.resize(), 0);
			},
			onChartTabChange(key) {
				this.chartTabKey = key;
				this.$nextTick(() => {
					if (key === 'income') this.initIncomeChart();
					else if (key === 'expense') this.initExpenseChart();
					this.resizeCharts();
				});
			},
			resizeCharts() {
				if (this.chartExpenseInstance) this.chartExpenseInstance.resize();
				if (this.chartIncomeInstance) this.chartIncomeInstance.resize();
			}
		},
		mounted() {
			this.resizeHandler = () => this.resizeCharts();
			window.addEventListener('resize', this.resizeHandler);
		},
		beforeDestroy() {
			if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
			this.disposeCharts();
		}
	};
</script>

<style lang="less" scoped>
	.detail-content {
		min-height: 200px;
	}
	.detail-section {
		background-color: #fff;
		padding: 24px;

	}
	.detail-card {
		margin-bottom: 16px;
	}

	.profile-header {
        margin-bottom: 16px;
		.profile-header-row {
			display: flex;
			align-items: center;
			gap: 12px;
		}
		.profile-header-col {
			min-width: 0;
		}
		
		.photo-gallery-in-header {
			margin-top: 0;
			grid-template-columns: repeat(3, 1fr);
			gap: 8px;
			.photo-item {
				height: 72px;
			}
		}
		.avatar-wrap {
			.avatar {
				width: 120px;
				height: 120px;
				object-fit: cover;
				border-radius: 8px;
			}
			.avatar-placeholder {
				width: 120px;
				height: 120px;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				background: #f0f0f0;
				color: #999;
				font-size: 12px;
				border-radius: 8px;
			}
		}
		.profile-name-line {
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
			white-space: nowrap;
			gap: 8px;
			margin-bottom: 8px;
			.tag-vest {
				color: #999;
			}
			.nickname {
				font-weight: bold;
				font-size: 16px;
			}
			.badge {
				display: inline-block;
				padding: 0 6px;
				font-size: 12px;
				border-radius: 2px;
			}
			.badge-certified {
				background: #f6ffed;
				color: #52c41a;
				border: 1px solid #b7eb8f;
			}
			.badge-uncertified {
				background: #f5f5f5;
				color: #8c8c8c;
			}
			.badge-vip {
				background: #fff1f0;
				color: #f5222d;
				font-weight: bold;
			}
			.voice-bar {
				display: inline-flex;
				align-items: center;
				gap: 10px;
				height: 24px;
				padding: 0 14px 0 6px;
				background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
				border-radius: 16px;
				box-shadow: 0 2px 8px rgba(24, 144, 255, 0.35);
				cursor: pointer;
				user-select: none;
				.voice-bar-btn {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 16px;
					height: 16px;
					background: rgba(255, 255, 255, 0.95);
					border-radius: 50%;
				}
				.voice-bar-play-icon {
					width: 0;
					height: 0;
					margin-left: 2px;
					border-style: solid;
					border-width: 5px 0 5px 9px;
					border-color: transparent transparent transparent #1890ff;
				}
				.voice-bar-pause-icon {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 2px;
					&::before,
					&::after {
						content: '';
						width: 3px;
						height: 10px;
						background: #1890ff;
						border-radius: 2px;
					}
				}
				.voice-bar-strip {
					display: flex;
					align-items: flex-end;
					gap: 4px;
					height: 12px;
				}
				.voice-bar-dot {
					width: 4px;
					min-height: 3px;
					background: rgba(255, 255, 255, 0.85);
					border-radius: 2px;
				}
				&.voice-bar--playing .voice-bar-dot {
					background: #fff;
				}
				&.voice-bar--disabled {
					cursor: not-allowed;
					opacity: 0.55;
					box-shadow: none;
				}
			}
		}
		.profile-basic {
			margin-bottom: 4px;
			color: #666;
			font-size: 13px;
			span { margin-right: 12px; }
		}
		.profile-location {
			margin-bottom: 8px;
			color: #666;
			font-size: 13px;
		}
		.profile-signature {
			font-size: 12px;
			color: #666;
			line-height: 1.5;
			&.placeholder {
				color: #bbb;
			}
		}
	}

	.detail-grid,
	.chat-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 12px 16px;
		.detail-item {
			.label { color: #666; margin-right: 6px; }
			.val { color: #333; }
		}
	}
	.chat-grid {
		grid-template-columns: repeat(3, 1fr);
	}

	.photo-gallery {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-top: 16px;
        margin-left: 36px;
		.photo-item {
			height: 80px;
			background: #fafafa;
			border-radius: 4px;
			display: flex;
			align-items: center;
			justify-content: center;
			overflow: hidden;
			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
			.photo-placeholder {
				color: #bbb;
				font-size: 12px;
			}
		}
	}

	.chart-section {
		margin-bottom: 16px;
		padding-top: 0;
		padding-bottom: 8px;
	}

	.chart-card {
		.chart-container {
			width: 100%;
			height: 280px;
		}
	}

	.user-detail-page--embedded {
		.detail-section {
			padding: 16px 0;
		}
		.chart-section {
			padding: 0 0 8px;
		}
	}

	.record-tabs-card {
		::v-deep .ant-tabs-bar {
			margin-bottom: 16px;
		}
	}
	.record-tabs-card-full {
		width: 100%;
	}
	.record-user-cell {
		display: flex;
		align-items: center;
		gap: 8px;
		.record-user-avatar {
			width: 40px;
			height: 40px;
			object-fit: cover;
			border-radius: 4px;
		}
		.record-user-avatar-placeholder {
			width: 40px;
			height: 40px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			background: #f0f0f0;
			color: #999;
			font-size: 12px;
			border-radius: 4px;
		}
		.record-user-info {
			display: flex;
			flex-direction: column;
			font-size: 13px;
			.record-user-meta {
				color: #999;
				font-size: 12px;
				margin-top: 2px;
			}
		}
	}
	.record-complex {
		p { margin: 0; }
		.assist {
			font-size: 12px;
			color: #999;
		}
	}
	.success { color: #52c41a; }
	.wait { color: #faad14; }
	.error { color: #f5222d; }
	.income-span { color: #00b900; }
	.expend-span { color: #ff0000; }

    ::v-deep .ant-page-header {
        padding: 16px 0;
    }
</style>
